import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { getMyFavorites, getRdvCounts, type AssignedClient } from '../../lib/refonteApi';
import CommandPalette from './CommandPalette';

// ── Menus par rôle (miroir du prototype validé 2026-07-22) ──────────────
// `to` = route réelle ; `soon` = écran pas encore construit (désactivé).
type Item = { label: string; to?: string; soon?: boolean; kbd?: string; badge?: 'rdv' };
type Section = { section: string } | Item;

const MENUS: Record<string, Section[]> = {
  sdr: [
    // Organisé par JOB (audit UX 09/10) : le travail du jour d'abord, une seule entrée Rendez-vous.
    { section: 'Mon travail' },
    { label: 'Ma journée', to: '/app/ma-journee', kbd: '1' },
    { label: 'Rendez-vous', to: '/app/rdv-a-statuer', badge: 'rdv' },
    { label: 'Production SDR', to: '/app/production' },
    { label: 'Conversations & perf', to: '/app/echanges' },
    { section: 'Mes clients' },
    { label: 'Mes clients', to: '/app/clients' },
    { label: 'Demande de liste', to: '/app/demande-liste' },
    { label: 'Agenda', to: '/app/agenda' },
  ],
  csm: [
    { section: 'Mon travail' },
    { label: 'Santé clients', to: '/app/sante' },
    { label: 'Rendez-vous', to: '/app/rdv-a-statuer', badge: 'rdv' },
    { label: 'À valider (facturation)', to: '/app/rdv-a-valider' },
    { label: 'Agenda', to: '/app/agenda' },
    { section: 'Clients' },
    { label: 'Registre clients', to: '/app/clients' },
    { label: 'Onboarding', to: '/app/onboarding' },
    { label: 'Campagnes mail & LinkedIn', to: '/app/campagnes' },
    { label: 'Demande de liste', to: '/app/demande-liste' },
    { label: 'Utilisateurs clients', to: '/app/utilisateurs-clients' },
    { section: 'Pilotage' },
    { label: 'Production SDR', to: '/app/production' },
    { label: "Stats d'équipe", to: '/app/stats-equipe' },
    { label: 'Conversations & perf', to: '/app/echanges' },
    { label: 'Performance & marché', to: '/app/portail/performance' },
    { label: 'Données à compléter', to: '/app/donnees' },
    { label: 'Équipe & rôles', to: '/app/equipe' },
    { label: 'Guide : accès & onboarding', to: '/app/aide' },
  ],
  founder: [
    { section: 'Direction' },
    { label: 'Business plan', to: '/app/bp' },
    { label: 'Facturation', to: '/app/facturation' },
    { label: 'Santé clients', to: '/app/sante' },
    { section: 'Clients' },
    { label: 'Registre clients', to: '/app/clients' },
    { label: 'Rendez-vous', to: '/app/rdv-a-statuer', badge: 'rdv' },
    { label: 'À valider (facturation)', to: '/app/rdv-a-valider' },
    { label: 'Onboarding', to: '/app/onboarding' },
    { label: 'Demande de liste', to: '/app/demande-liste' },
    { label: 'Utilisateurs clients', to: '/app/utilisateurs-clients' },
    { section: 'Pilotage' },
    { label: 'Production SDR', to: '/app/production' },
    { label: "Stats d'équipe", to: '/app/stats-equipe' },
    { label: 'Conversations & perf', to: '/app/echanges' },
    { label: 'Agenda', to: '/app/agenda' },
    { label: 'Performance & marché', to: '/app/portail/performance' },
    { label: 'Équipe & rôles', to: '/app/equipe' },
    { label: 'Guide : accès & onboarding', to: '/app/aide' },
  ],
  client: [
    { section: 'Mon espace' },
    { label: 'Tableau de bord', to: '/app/portail' },
    { label: 'Journées de prospection', to: '/app/portail/journees' },
    { label: 'Registre des RDV', to: '/app/portail/registre' },
    { label: 'Registre des campagnes', to: '/app/portail/campagnes' },
    { label: 'Performance & marché', to: '/app/portail/performance' },
    { section: 'À venir' },
    { label: 'Conversations', soon: true },
    { label: 'Mon calibrage & fichiers', soon: true },
    { label: 'Ma facturation', soon: true },
  ],
};

// mapping profil plateforme → menu. Les prospecteurs (role admin mais prospecting_team
// renseignée) voient le front SDR/GTM, pas le menu manager. Marche aussi après bascule 'sdr'.
function menuKeyFor(profile?: { role?: string; prospecting_team?: string | null } | null): keyof typeof MENUS {
  const role = profile?.role;
  if (role === 'client') return 'client';
  if (role === 'super_admin') return 'founder'; // super_admin voit la vue direction
  if (role === 'sdr') return 'sdr';
  if (profile?.prospecting_team === 'cold_call' || profile?.prospecting_team === 'gtm') return 'sdr';
  return 'csm'; // admin sans équipe = CSM/manager
}

const RefonteShell: React.FC = () => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  // Sélecteur de vue : permet à un interne de basculer entre les fronts (SDR / CSM / Direction)
  // sans changer ses droits. Utile quand un même profil porte plusieurs casquettes (ex. un SDR
  // qui doit aussi voir la vue CSM). super_admin voit tout ; admin bascule SDR ↔ CSM.
  const isSuperAdmin = profile?.role === 'super_admin';
  const canSwitchView = isSuperAdmin || profile?.role === 'admin';
  const [viewAs, setViewAs] = useState<keyof typeof MENUS | ''>('');
  const effectiveKey = (viewAs || menuKeyFor(profile)) as keyof typeof MENUS;
  // Fidélité du mode « Client (aperçu) » (review Charles 09/10) : un interne qui bascule en vue
  // client doit voir EXACTEMENT ce que voit un client (pas d'évaluations internes, pas de boutons
  // internes). Le mode aperçu est porté par ?asclient=1 → on le propage depuis le menu.
  const isInternalViewingClient = viewAs === 'client' && profile?.role !== 'client';
  const linkTo = (to: string) =>
    isInternalViewingClient && to.startsWith('/app/portail')
      ? `${to}${to.includes('?') ? '&' : '?'}asclient=1`
      : to;
  const menu = MENUS[effectiveKey];
  // On masque les écrans « bientôt » (décision 24/09 : roadmap propre plus tard) et
  // on retire les entêtes de section qui se retrouvent sans aucun item visible.
  const visibleMenu = (() => {
    const kept = menu.filter(it => ('section' in it) || (it.to && !it.soon));
    return kept.filter((it, idx) => {
      if (!('section' in it)) return true;
      const next = kept[idx + 1];
      return !!next && !('section' in next);
    });
  })();
  // Badge « travail en attente » sur l'entrée Rendez-vous (RDV passés à statuer + à confirmer).
  const [rdvBadge, setRdvBadge] = useState(0);
  useEffect(() => {
    if (profile?.role === 'client') return;
    getRdvCounts().then(c => setRdvBadge(c.aStatuer + c.aConfirmer)).catch(() => {});
  }, [profile?.role]);

  // Sous-niveau « clients affectés » sous « Mes clients » (les clients que l'utilisateur prospecte).
  const [favs, setFavs] = useState<AssignedClient[]>([]);
  useEffect(() => {
    const uid = profile?.user_id; if (!uid) return;
    const reload = () => getMyFavorites(uid).then(setFavs).catch(() => setFavs([]));
    reload();
    window.addEventListener('favorites-changed', reload);
    return () => window.removeEventListener('favorites-changed', reload);
  }, [profile?.user_id]);
  // Un prospecteur GTM voit le menu 'sdr' mais on affiche « GTM » comme métier.
  const roleLabel =
    (!viewAs && effectiveKey === 'sdr' && profile?.prospecting_team === 'gtm')
      ? 'GTM'
      : ({ client: 'Client', sdr: 'SDR', csm: 'CSM / Manager', founder: 'Direction' } as Record<string, string>)[
          effectiveKey
        ] || 'Équipe';

  return (
    <div className="refonte-scope min-h-screen flex bg-cream text-ink">
      {/* ⌘K — palette de recherche globale (pages du menu courant + clients) */}
      <CommandPalette pages={visibleMenu.filter((it: any) => !('section' in it) && it.to).map((it: any) => ({ label: it.label, to: it.to, hint: 'page' }))} />
      {/* Sidebar */}
      <aside className="w-56 shrink-0 bg-hook-dark text-white flex flex-col sticky top-0 h-screen px-3 py-5">
        <div className="flex items-center gap-2 px-2.5 pb-1.5">
          <img src="/hook-logo.png" alt="Hook" className="h-8 w-8 object-contain bg-white rounded-md p-0.5" />
          {profile?.role === 'client'
            ? <><span className="font-extrabold text-[16px] tracking-tight">Hook</span><span className="ml-auto font-mono text-[9px] text-neutral-500 font-medium tracking-widest">Espace client</span></>
            : <><span className="font-extrabold text-[16px] tracking-tight">hooklab</span><span className="ml-auto font-mono text-[9px] text-neutral-500 font-medium tracking-widest">OS·v2</span></>}
        </div>
        <div className="flex items-center gap-2 font-mono text-[9.5px] text-neutral-400 px-2.5 pb-4">
          <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          {roleLabel.toUpperCase()}
        </div>

        {canSwitchView && (
          <div className="px-2.5 pb-3">
            <label className="font-mono text-[8.5px] uppercase tracking-widest text-neutral-600 block mb-1">Vue</label>
            <select
              value={viewAs}
              onChange={(e) => setViewAs(e.target.value as keyof typeof MENUS | '')}
              className="w-full bg-hook-dark-2 text-neutral-200 text-[12px] rounded-md px-2 py-1.5 border border-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose"
            >
              <option value="">Ma vue</option>
              <option value="sdr">SDR</option>
              <option value="csm">CSM / Manager</option>
              {isSuperAdmin && <option value="founder">Direction</option>}
              {isSuperAdmin && <option value="client">Client (aperçu)</option>}
            </select>
          </div>
        )}

        <nav aria-label="Navigation refonte" className="flex flex-col gap-0.5 flex-1 overflow-y-auto">
          {visibleMenu.map((it, i) =>
            'section' in it ? (
              <div key={i} className="font-mono text-[9.5px] uppercase tracking-widest text-neutral-600 px-2.5 pt-3.5 pb-1.5 font-semibold">
                {it.section}
              </div>
            ) : it.soon || !it.to ? (
              <button
                key={i}
                aria-label={`${it.label} — écran pas encore construit`}
                onClick={() => alert(`Écran "${it.label}" — pas encore construit (à venir dans un prochain lot).`)}
                className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-neutral-500 hover:text-white hover:bg-hook-dark-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose"
              >
                {it.label}
                <span className="ml-auto font-mono text-[9.5px] text-neutral-600 border border-neutral-700 rounded px-1.5">bientôt</span>
              </button>
            ) : (
              <React.Fragment key={i}>
              <NavLink
                to={linkTo(it.to)}
                end
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] transition-colors border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose ${
                    isActive ? 'bg-hook-dark-2 text-white border-white/10' : 'text-neutral-300 hover:text-white hover:bg-hook-dark-2'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span aria-hidden className="w-[3px] h-3.5 rounded bg-hook-rose -ml-1.5" />}
                    {it.label}
                    {it.badge === 'rdv' && rdvBadge > 0 && (
                      <span className="ml-auto font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-hook-rose/20 text-hook-rose">{rdvBadge}</span>
                    )}
                    {it.kbd && <span className="ml-auto font-mono text-[9.5px] text-neutral-500 border border-neutral-700 rounded px-1.5">{it.kbd}</span>}
                  </>
                )}
              </NavLink>
              {it.to === '/app/clients' && favs.length > 0 && (
                <div className="ml-3 pl-2 border-l border-white/10 flex flex-col gap-0.5 mt-0.5 mb-1">
                  <div className="font-mono text-[8px] uppercase tracking-widest text-amber-400/70 px-2 py-0.5">★ Favoris</div>
                  {favs.map(c => (
                    <NavLink key={c.id} to={`/app/clients/${c.id}`}
                      className={({ isActive }) => `flex items-center gap-1.5 px-2 py-1 rounded-md text-[11.5px] transition-colors ${isActive ? 'bg-hook-dark-2 text-white' : 'text-neutral-300 hover:text-white hover:bg-hook-dark-2'}`}>
                      <span className="text-amber-400 text-[9px]">★</span>
                      <span className="truncate">{c.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
              </React.Fragment>
            )
          )}
        </nav>

        <div className="mt-auto pt-3 border-t border-white/10">
          <div className="px-2.5 py-2 text-[12px] text-neutral-400 truncate">
            {profile?.full_name || 'Utilisateur'}
          </div>
          <button
            onClick={async () => { await signOut(); navigate('/login'); }}
            className="w-full text-left px-2.5 py-1.5 rounded-lg text-[12px] text-neutral-400 hover:text-white hover:bg-hook-dark-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hook-rose"
          >
            Se déconnecter
          </button>
          {/* Mention technique réservée à l'interne : anxiogène et sans valeur pour un client (audit UX 09/10 #5) */}
          {profile?.role !== 'client' && (
            <div className="px-2.5 pt-3 font-mono text-[9px] text-neutral-600 leading-relaxed">
              {import.meta.env.VITE_SUPABASE_URL?.includes('uieihxbaysigapbnkmql')
                ? <>ENV · DEV<br />DONNÉES RÉELLES (refonte-dev)</>
                : <>ENV · PROD<br />DONNÉES RÉELLES (prod)</>}
            </div>
          )}
        </div>
      </aside>

      {/* Contenu — prend toute la largeur disponible */}
      <main className="flex-1 px-8 py-7 w-full max-w-[1800px] mx-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default RefonteShell;
